<template>
  <div class="min-h-initial max-h-initial overflow-hidden text-gray-800">
    <div
      ref="scrollRef"
      class="w-full h-full flex flex-col px-[14px] py-[22px] tablet-h:py-[30px] relative overflow-auto"
      :style="componentStyles"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <div class="flex items-center justify-between mb-[26px]">
          <RouterLink to="/infringement">
            <NewLink type="breadcrumb">Comparison analyses</NewLink>
          </RouterLink>

          <div class="flex items-center gap-x-6 tablet-h:hidden">
            <template v-if="canUseOwnerActions">
              <Icon
                name="ThumbsUp"
                :size="18"
                :class="
                  feedbackState === FeedbackRating.Up
                    ? 'stroke-green-600'
                    : 'stroke-gray-400'
                "
                @click="handleThumbsUp"
              />
              <Icon
                name="ThumbsUp"
                :size="18"
                :class="[
                  feedbackState === FeedbackRating.Down
                    ? 'stroke-red-600'
                    : 'stroke-gray-400',
                  'rotate-180',
                ]"
                @click="handleThumbsDown"
              />
            </template>
            <Icon
              v-if="canDownloadReport"
              name="Download"
              :size="18"
              class="stroke-gray-400"
            />
            <Icon
              v-if="canUseOwnerActions"
              name="Share"
              :size="18"
              class="stroke-gray-400 cursor-pointer hover:stroke-gray-600"
              @click="handleShare"
            />
          </div>
        </div>

        <div
          class="flex items-center justify-between mb-[26px] tablet-h:mb-[30px]"
        >
          <div>
            <div
              class="font-primary font-medium text-[22px] leading-[18px] mb-[13px] tablet-h:mb-2"
            >
              {{ title }}
            </div>

            <div class="font-secondary text-[14px] leading-[18px]">
              Created on {{ formatTimestamp(report?.timestamp) }}
            </div>
          </div>

          <div class="hidden items-center gap-x-6 tablet-h:flex">
            <template v-if="canUseOwnerActions">
              <Icon
                name="ThumbsUp"
                :size="18"
                :class="[
                  feedbackState === FeedbackRating.Up
                    ? 'stroke-green-600'
                    : 'stroke-gray-400',
                  'cursor-pointer',
                ]"
                @click="handleThumbsUp"
              />
              <Icon
                name="ThumbsUp"
                :size="18"
                :class="[
                  feedbackState === FeedbackRating.Down
                    ? 'stroke-red-600'
                    : 'stroke-gray-400',
                  'rotate-180 cursor-pointer',
                ]"
                @click="handleThumbsDown"
              />
            </template>
            <Icon
              v-if="canDownloadReport"
              name="Download"
              :size="18"
              class="stroke-gray-400"
            />
            <Icon
              v-if="canUseOwnerActions"
              name="Share"
              :size="18"
              class="stroke-gray-400 cursor-pointer hover:stroke-gray-600"
              @click="handleShare"
            />
          </div>
        </div>

        <Card.Base class="mb-[60px]">
          <Card.Title icon="Info" class="mb-[25px]">
            Technical analysis overview
          </Card.Title>

          <Card.Content>
            <div
              class="grid grid-cols-1 mobile-l:grid-cols-2 tablet-h:grid-cols-3 gap-x-5 gap-y-[25px]"
            >
              <div class="bg-gray-200/20 rounded-xl p-5 pt-[14px]">
                <div
                  class="font-secondary font-medium text-[14px] leading-[22px] text-gray-900 mb-[18px]"
                >
                  Global match percentage
                </div>

                <div
                  class="size-[120px] tablet-h:size-[170px] relative flex items-center justify-center mx-auto rounded-full border-16 tablet-h:border-20 border-[#D8D8D8]"
                >
                  <svg
                    class="size-[120px] tablet-h:size-[170px] absolute -rotate-90"
                    :class="{
                      'stroke-red-500': report?.level === ScoreLevel.High,
                      'stroke-yellow-500': report?.level === ScoreLevel.Medium,
                      'stroke-green-500': report?.level === ScoreLevel.Low,
                    }"
                  >
                    <circle
                      class="fill-transparent stroke-16 tablet-h:stroke-20"
                      :r="actualRadius"
                      :cx="radius"
                      :cy="radius"
                      :stroke-dasharray="`${circumference} ${circumference}`"
                      :stroke-dashoffset="strokeDashoffset"
                    />
                  </svg>

                  <div
                    class="font-primary font-medium text-center"
                    :class="{
                      'text-red-500': report?.level === ScoreLevel.High,
                      'text-yellow-500': report?.level === ScoreLevel.Medium,
                      'text-green-500': report?.level === ScoreLevel.Low,
                    }"
                  >
                    <div
                      class="text-[22px] tablet-h:text-[34px] leading-[28px] tablet-h:leading-[50px]"
                    >
                      {{ report?.score }}%
                    </div>

                    <div
                      class="text-[11px] tablet-h:text-[18px] leading-[13px] tablet-h:leading-[20px]"
                    >
                      match
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-gray-200/20 rounded-xl p-5 pt-[14px]">
                <div
                  class="font-secondary font-medium text-[14px] leading-[22px] text-gray-900 mb-[5px] tablet-h:mb-2"
                >
                  Potential infringements
                </div>

                <div
                  class="font-primary text-[40px] leading-[40px] tablet-h:leading-[50px] font-medium text-gray-900 mb-2"
                >
                  {{ report?.matches.total }}
                  <span class="hidden tablet-h:inline-block">matches</span>
                </div>

                <div class="flex flex-col gap-y-[3px]">
                  <div
                    v-for="category in matchCategories"
                    :key="category.type"
                    class="flex items-center gap-x-2"
                    @click="handleCategoryClick(category.type)"
                  >
                    <div
                      class="hidden tablet-h:flex size-4 items-center justify-center"
                    >
                      <Icon
                        :name="getIconName(category.type)"
                        :size="
                          category.type === MatchCategoryType.Video ? 12 : 16
                        "
                        class="stroke-gray-800"
                      />
                    </div>
                    <NewLink type="secondary">
                      {{ category.breakdown.length }}
                      {{ category.type.toLowerCase() }}
                      {{ pluralize('match', category.breakdown.length) }}
                    </NewLink>
                  </div>
                </div>
              </div>

              <div
                class="col-span-full tablet-h:col-span-1 bg-gray-200/20 rounded-xl p-5 pt-[14px]"
              >
                <div
                  class="font-secondary font-medium text-[14px] leading-[22px] text-gray-900 mb-[5px] tablet-h:mb-2"
                >
                  Comparing
                </div>
                <div class="flex flex-col gap-y-1">
                  <div class="flex items-center gap-x-2.5">
                    <ThumbnailIcon
                      class="min-w-[70px] tablet-h:min-w-[80px] max-w-[70px] tablet-h:max-w-[80px] h-[70px] tablet-h:h-20 bg-gray-200/25 rounded-[3px] p-[7px]"
                      :icon-size="iconSize"
                      :type="report?.copyright.type"
                      :image-url="report?.copyright.asset?.previewImageUrl"
                    />
                    <div class="w-full flex flex-col gap-y-1">
                      <div
                        class="font-secondary text-[12px] leading-[16px] text-gray-500"
                      >
                        Your registered work
                      </div>
                      <div
                        v-if="report?.copyright.name"
                        v-truncate
                        class="w-full font-secondary text-[14px] leading-[18px]"
                      >
                        <div data-truncate-target>
                          {{ report.copyright.name }}
                        </div>
                      </div>
                      <RouterLink
                        :to="`/file/${report?.copyright.copyright.id}`"
                        class="font-primary text-[14px] leading-[22px] text-link"
                      >
                        View registration
                      </RouterLink>
                    </div>
                  </div>

                  <div class="w-[70px] tablet-h:w-20 flex justify-center">
                    <Icon name="Sort" :size="18" class="stroke-gray-800" />
                  </div>

                  <div class="flex items-center gap-x-2.5">
                    <ThumbnailIcon
                      class="min-w-[70px] tablet-h:min-w-[80px] max-w-[70px] tablet-h:max-w-[80px] h-[70px] tablet-h:h-20 bg-gray-200/25 rounded-[3px] p-[7px]"
                      :icon-size="iconSize"
                      :type="report?.infringingFile.type"
                      :image-url="report?.infringingFile.asset.previewImageUrl"
                    />
                    <div class="w-full flex flex-col gap-y-1">
                      <div
                        class="font-secondary text-[12px] leading-[16px] text-gray-500"
                      >
                        Potential infringing work
                      </div>
                      <div v-if="report?.infringingFile.name" v-truncate>
                        <div
                          data-truncate-target
                          class="font-secondary text-[14px] leading-[18px]"
                        >
                          {{ report?.infringingFile.name }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Card.Separator />

            <div
              class="font-secondary font-medium text-[14px] leading-[22px] mb-[15px]"
            >
              Infringment findings
            </div>
            <div class="font-secondary text-[14px] leading-[20px]">
              {{ report?.summary }}
            </div>

            <Card.Separator />

            <div
              class="font-secondary font-medium text-[14px] leading-[22px] mb-[25px]"
            >
              Potential infringing work data
            </div>

            <Attributes
              v-if="report?.infringingFile"
              :attributes="report.infringingFile.attributes"
            />

            <Card.Separator />

            <div v-if="report?.infringingFile" v-truncate>
              <div
                class="font-secondary font-medium text-[14px] leading-[22px]"
                data-truncate-target
              >
                File preview: {{ report?.infringingFile.name }}
              </div>
            </div>

            <div
              class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-3"
            >
              Pages: 13
            </div>

            <div
              class="w-full h-full bg-[#FAFAFA] rounded-xl p-2.5 tablet-h:h-[414px] relative"
            >
              <Player
                v-if="canPlayReportMedia && infringingStreamUrl"
                :url="infringingStreamUrl"
                :type="report!.infringingFile.type"
              />
              <ThumbnailIcon
                v-else
                class="w-full h-full"
                :icon-size="previewIconSize"
                :type="report?.infringingFile.type"
                :image-url="report?.infringingFile.asset.previewImageUrl"
              />
            </div>

            <div v-if="report?.timeline && timelineData" ref="timelineRef">
              <TimelineCompare
                :infringing="timelineData.infringing"
                :registered="timelineData.registered"
                :matches="timelineMatches"
                class="mt-[25px]"
                @view="handleTimelineView"
              />
            </div>
          </Card.Content>
        </Card.Base>

        <div class="flex flex-col gap-y-[60px]">
          <div
            v-for="category in report?.matches.categories"
            :key="category.type"
            :id="category.type"
          >
            <div v-if="category.breakdown.length">
              <div class="flex items-center gap-x-2.5">
                <div
                  class="size-[30px] rounded-full bg-red-200/50 flex items-center justify-center mr-2.5"
                >
                  <Icon name="Alert" :size="16" class="stroke-red-700" />
                </div>

                <div
                  class="font-primary font-semibold text-[24px] leading-[28px]"
                >
                  {{ category.breakdown.length }}
                  {{ category.type.toLowerCase() }}
                  {{ pluralize('match', category.breakdown.length) }}
                </div>
              </div>

              <div class="w-full h-px bg-gray-200/50 my-[25px]"></div>

              <template v-if="report">
                <div
                  v-for="(match, index) in category.breakdown"
                  :key="`${category.type}-${index}`"
                  :id="`match-${matchScrollId(match)}`"
                  class="mb-[25px] scroll-mt-[80px]"
                >
                  <MobileMatch
                    :type="category.type"
                    :match="match"
                    :number="index + 1"
                    :allow-playback="canPlayReportMedia"
                    class="tablet-h:hidden"
                  />
                  <Match
                    :type="category.type"
                    :match="match"
                    :number="index + 1"
                    :allow-playback="canPlayReportMedia"
                    class="hidden tablet-h:grid"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BackPill
      :visible="backPill.visible"
      :label="backPill.label"
      @click="handleBackPillClick"
    />
  </div>
</template>

<script setup lang="ts">
import Player from '@/features/copyright/components/Player.vue'
import BackPill from '@/features/infringement/components/BackPill.vue'
import TimelineCompare from '@/features/infringement/components/TimelineCompare.vue'
import type { TimelineMatch } from '@/features/infringement/components/timeline-types'
import Attributes from '@/shared/components/Attributes.vue'
import Match from '@/features/infringement/components/Match.vue'
import MobileMatch from '@/features/infringement/components/MobileMatch.vue'
import NewLink from '@/shared/components/NewLink.vue'
import { graphql } from '@/shared/graphql/router'
import {
  ScoreLevel,
  MatchCategoryType,
  FeedbackRating,
  FeedbackType,
  ReportRole,
} from '@/shared/graphql/router/graphql'
import { useDevice } from '@/shared/composables/useDevice'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { formatTimestamp } from '@/shared/utils/datetime'
import { Card, Icon, ThumbnailIcon, type IconName } from '@dacr/ui'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, onBeforeUnmount, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/shared/stores/user'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import pluralize from 'pluralize'

const device = useDevice()
const { availableHeight } = useAvailableHeight()
const route = useRoute()
const userStore = useUserStore()

const { result } = useQuery(
  graphql(`
    query Report($id: ID!) {
      report(id: $id) {
        id
        role
        timestamp
        copyright {
          id
          name
          type
          metadata {
            originalFilename
          }
          asset {
            previewImageUrl
          }
          copyright {
            id
          }
          analysis {
            ... on VideoAnalysis {
              duration
            }
          }
        }
        infringingFile {
          id
          name
          type
          attributes {
            key
            value
          }
          asset {
            previewImageUrl
            ... on DocumentAsset {
              totalPages
              documentUrl
            }
            ... on StreamAsset {
              streamUrl
            }
          }
        }
        summary
        score
        level
        matches {
          total
          categories {
            type
            breakdown {
              ...MatchBreakdown
            }
          }
        }
        timeline {
          infringingFile {
            transcript
            audioUrl
            frames
            duration
          }
          copyright {
            transcript
            audioUrl
            frames
            duration
          }
        }
        feedback
        settings {
          isPrivate
        }
        viewers(input: { first: 100, filterBy: ALL }) {
          totalCount
          edges {
            cursor
            role
            node {
              ... on User {
                id
                name
                username
                avatar {
                  url
                }
              }
              ... on Organization {
                id
                name
                avatar {
                  url
                }
                owner {
                  ... on User {
                    id
                    name
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  `),
  {
    id: route.params.id as string,
  },
  {
    clientId: 'router',
  }
)

const { mutate: createFeedback } = useMutation(
  graphql(`
    mutation CreateFeedback($input: CreateFeedbackInput!) {
      createFeedback(input: $input) {
        success
        feedbackId
      }
    }
  `),
  {
    clientId: 'router',
  }
)

const feedbackState = ref<FeedbackRating | null>(null)

const handleFeedback = (rating: FeedbackRating, message?: string) => {
  if (!report.value || !userStore.user) return

  createFeedback({
    input: {
      rating,
      type: FeedbackType.Report,
      referenceId: report.value.id,
      message,
    },
  })
}

const handleThumbsUp = () => {
  if (!canUseOwnerActions.value) return
  feedbackState.value = FeedbackRating.Up
  handleFeedback(FeedbackRating.Up)
}

const { open } = useDialog()

const handleThumbsDown = () => {
  if (!canUseOwnerActions.value) return
  open(Dialogs.ReportFeedback, {
    events: {
      success(data?: unknown) {
        feedbackState.value = FeedbackRating.Down
        handleFeedback(FeedbackRating.Down, data as string)
      },
    },
  })
}

const handleShare = () => {
  if (!report.value || !canUseOwnerActions.value) return
  open(Dialogs.ShareReport, {
    props: {
      report: report.value,
      getReport: () => report.value,
    },
  })
}

const componentStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const radius = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 85 : 60
)

const actualRadius = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value
    ? radius.value - 10
    : radius.value - 8
)

const circumference = computed(() => actualRadius.value * 2 * Math.PI)

const strokeDashoffset = computed(
  () =>
    circumference.value -
    ((report.value?.score || 0) / 100) * circumference.value
)

const title = computed(() => {
  if (device.isMobile.value || device.isTabletVertical.value) {
    return 'Comparison analysis'
  }

  return 'Technical comparison analysis report'
})

const iconSize = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 56 : 66
)

const previewIconSize = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 262 : 300
)

const report = computed(() => result.value?.report)

const canUseOwnerActions = computed(
  () => report.value?.role === ReportRole.Owner
)

const isPublicReport = computed(() => report.value?.role === ReportRole.Public)

const canDownloadReport = computed(
  () => !!report.value && !isPublicReport.value
)

const canPlayReportMedia = computed(() => !isPublicReport.value)

const infringingStreamUrl = computed(() => {
  const asset = report.value?.infringingFile.asset
  if (asset?.__typename === 'StreamAsset') return asset.streamUrl
  return undefined
})

provide(
  'timeline',
  computed(() => report.value?.timeline)
)

provide(
  'copyrightDuration',
  computed(() => {
    const analysis = report.value?.copyright.analysis
    if (analysis?.__typename === 'VideoAnalysis') {
      return analysis.duration
    }
    return undefined
  })
)

const MATCH_TYPE_MAP: Partial<
  Record<MatchCategoryType, TimelineMatch['type']>
> = {
  [MatchCategoryType.Audio]: 'Audio',
  [MatchCategoryType.Video]: 'Video',
  [MatchCategoryType.Transcript]: 'Transcript',
  [MatchCategoryType.Text]: 'Text',
  [MatchCategoryType.Image]: 'Image',
}

const timelineMatches = computed<TimelineMatch[]>(() => {
  if (!report.value) return []
  const out: TimelineMatch[] = []

  for (const category of report.value.matches.categories) {
    const type = MATCH_TYPE_MAP[category.type]
    if (!type) continue

    let indexInCategory = 0
    for (const breakdown of category.breakdown) {
      indexInCategory++
      const b = breakdown as Record<string, Record<string, unknown>>
      const inf = b.infringingFile
      const reg = b.copyright

      const infStart = Number(inf?.start ?? NaN)
      const infEnd = Number(inf?.end ?? NaN)
      const regStart = Number(reg?.start ?? NaN)
      const regEnd = Number(reg?.end ?? NaN)

      if (
        Number.isFinite(infStart) &&
        Number.isFinite(infEnd) &&
        Number.isFinite(regStart) &&
        Number.isFinite(regEnd)
      ) {
        out.push({
          id: String(inf?.id ?? `${type}-${out.length}`),
          type,
          level: (inf?.level as ScoreLevel) ?? ScoreLevel.High,
          title: String(inf?.title ?? type),
          score: Number(inf?.score ?? 0),
          number: indexInCategory,
          location: String(inf?.location ?? ''),
          markers: Array.isArray(inf?.markers)
            ? (inf.markers as TimelineMatch['markers'])
            : [],
          infringing: { start: infStart, end: infEnd },
          registered: { start: regStart, end: regEnd },
        })
      }
    }
  }

  return out
})

const matchScrollId = (breakdown: unknown) => {
  const b = breakdown as Record<string, Record<string, unknown>>
  return String(b.infringingFile?.id ?? '')
}

const timelineData = computed(() => {
  const r = report.value
  if (!r?.timeline) return null

  return {
    infringing: {
      duration: r.timeline.infringingFile.duration,
      audioUrl: r.timeline.infringingFile.audioUrl,
      transcript: r.timeline.infringingFile.transcript ?? undefined,
      frames: r.timeline.infringingFile.frames ?? undefined,
      label: 'Potential infringing work',
      title: r.infringingFile.name,
      icon: {
        type: r.infringingFile.type,
        imageUrl: r.infringingFile.asset.previewImageUrl,
      },
    },
    registered: {
      duration: r.timeline.copyright.duration,
      audioUrl: r.timeline.copyright.audioUrl,
      transcript: r.timeline.copyright.transcript ?? undefined,
      frames: r.timeline.copyright.frames ?? undefined,
      label: 'Registration',
      title: r.copyright.name,
      icon: {
        type: r.copyright.type,
        imageUrl: r.copyright.asset?.previewImageUrl,
      },
    },
  }
})

watch(
  report,
  (val) => {
    if (val?.feedback) feedbackState.value = val.feedback
  },
  { immediate: true }
)

const getIconName = (type: MatchCategoryType): IconName => {
  switch (type) {
    case MatchCategoryType.Text:
      return 'Text'
    case MatchCategoryType.Audio:
      return 'Audio'
    case MatchCategoryType.Transcript:
      return 'Text'
    case MatchCategoryType.Video:
      return 'HollowPlay'
    default:
      return 'Image'
  }
}

const matchCategories = computed(() =>
  report.value?.matches.categories.filter(
    (category) => category.breakdown.length
  )
)

const scrollRef = ref<HTMLElement>()
const timelineRef = ref<HTMLElement>()

type BackTarget = 'top' | 'timeline'

const backPill = ref<{
  visible: boolean
  label: string
  target: BackTarget
}>({
  visible: false,
  label: 'Back to top',
  target: 'top',
})

const handleCategoryClick = (type: MatchCategoryType) => {
  document.querySelector(`#${type}`)?.scrollIntoView({ behavior: 'smooth' })
  backPill.value = {
    visible: true,
    label: 'Back to top',
    target: 'top',
  }
}

const handleTimelineView = () => {
  backPill.value = {
    visible: true,
    label: 'Back to timeline',
    target: 'timeline',
  }
}

const handleBackPillClick = () => {
  if (backPill.value.target === 'top') {
    scrollRef.value?.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    timelineRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  backPill.value = { ...backPill.value, visible: false }
}

const SCROLL_TOP_THRESHOLD = 80

let observer: IntersectionObserver | null = null
let scrollHandler: (() => void) | null = null

const cleanupWatchers = () => {
  observer?.disconnect()
  observer = null
  if (scrollHandler && scrollRef.value) {
    scrollRef.value.removeEventListener('scroll', scrollHandler)
  }
  scrollHandler = null
}

const hidePill = () => {
  backPill.value = { ...backPill.value, visible: false }
  cleanupWatchers()
}

watch(
  () => [backPill.value.visible, backPill.value.target] as const,
  ([visible, target]) => {
    cleanupWatchers()
    if (!visible) return

    if (target === 'top') {
      const el = scrollRef.value
      if (!el) return
      let hasLeft = false
      scrollHandler = () => {
        if (el.scrollTop > SCROLL_TOP_THRESHOLD) {
          hasLeft = true
        } else if (hasLeft) {
          hidePill()
        }
      }
      el.addEventListener('scroll', scrollHandler, { passive: true })
    } else {
      const el = timelineRef.value
      const root = scrollRef.value
      if (!el || !root) return
      let hasLeft = false
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return
          if (!entry.isIntersecting) {
            hasLeft = true
          } else if (hasLeft) {
            hidePill()
          }
        },
        { root, threshold: 0.2 }
      )
      observer.observe(el)
    }
  },
  { flush: 'post' }
)

onBeforeUnmount(cleanupWatchers)
</script>
